/**
 * The query cache — and the retry policy, which is where 401 and 503 part company.
 *
 * The module's provider deliberately does not create a `QueryClient`, because embedded the
 * host already has one and a second cache is one the host's devtools cannot see and the
 * host's sign-out cannot clear. Standalone there is no host, so the shell is the one that
 * creates it. This is the whole shape of the slice in miniature: the shell supplies the
 * chrome a host would otherwise have supplied, and nothing else.
 *
 * `isRetryable` comes from the core rather than being restated here. It is true for a
 * network failure, a 5xx, a 503 and "an identical request is already in progress", and
 * false for everything else — notably false for `InsufficientStockError`, because the
 * stock will not have appeared in 300 ms and retrying only delays telling someone.
 *
 * A 401 is not retryable and must not be. Retrying it would hammer the service with a
 * credential it has already refused; the session layer answers it instead, exactly once,
 * by either re-authenticating or explaining why it will not.
 *
 * A 503 is given more attempts than anything else. `core/api/auth.py` returns 503 when the
 * identity provider is unreachable, which is a condition that resolves on its own; the
 * point of the whole 401/503 split is that the client waits it out rather than stampeding
 * the IdP. Six attempts with exponential backoff capped at 30 s is a little over a minute
 * of patience, after which the banner offers a manual retry rather than giving up quietly.
 */

import { QueryClient } from '@tanstack/react-query';
import { ServiceUnavailableError, isRetryable } from '@inai/inventory-core';

const MAX_RETRIES = 3;
const MAX_RETRIES_UNAVAILABLE = 6;

export function retryPolicy(failureCount: number, error: unknown): boolean {
  if (!isRetryable(error)) return false;
  const ceiling =
    error instanceof ServiceUnavailableError ? MAX_RETRIES_UNAVAILABLE : MAX_RETRIES;
  return failureCount < ceiling;
}

export function retryDelay(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 30_000);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: retryPolicy,
      retryDelay,
      staleTime: 30_000,
      // A warehouse screen left open on a scanner should not refetch the whole ledger
      // every time the window regains focus.
      refetchOnWindowFocus: false,
    },
    // Mutations are NOT given a retry here on purpose. Every write in the module goes
    // through `useIdempotentMutation`, which sets its own `retry` from `isRetryable` and,
    // critically, mints the idempotency key in the wrapper so a retry reuses it. A retry
    // policy imposed from out here would not have that guarantee.
  },
});
